import React, { useContext, useState, useMemo, useCallback, useEffect } from "react";
import { Input } from "@/modern/components/ui/input";
import { Button } from "@/modern/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/modern/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/modern/components/ui/dialog";
import { ScrollArea } from "@/modern/components/ui/scroll-area";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { Search } from "lucide-react";
import { cn } from "@/modern/utils";
import BryntumContext from "@/react/context/bryntumContext";
import { STORE_KEYS } from "@/other/constants/StoreKeys";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";
import { ILinkedEntity } from "@/react/schema/contactSchema";
import { UserAvatar } from "@/modern/components/user-avatar";
import { getEntityIcon } from "@/other/utils/utils.jsx";

// Map ENTITY_TYPES to capitalized versions used in the data
const ENTITY_TYPE_DISPLAY: Record<string, string> = {
  [ENTITY_TYPES.TASK]: "Task",
  [ENTITY_TYPES.RESOURCE]: "Resource",
  [ENTITY_TYPES.CLIENT]: "Client",
  [ENTITY_TYPES.USER]: "User",
};

interface LinkEntityEditorProps {
  initialLinkedData: ILinkedEntity[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (newLinkedData: ILinkedEntity[]) => void;
}

export const LinkEntityEditor: React.FC<LinkEntityEditorProps> = ({ initialLinkedData, open, onOpenChange, onSave }) => {
  const { project } = useContext(BryntumContext);
  const [searchTerm, setSearchTerm] = useState("");
  const [linkedEntities, setLinkedEntities] = useState<ILinkedEntity[]>(initialLinkedData);

  // Reset state when dialog closes or when initialLinkedData changes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
    } else {
      // Reset linkedEntities to initialLinkedData when dialog opens
      setLinkedEntities(initialLinkedData);
    }
  }, [open, initialLinkedData]);

  // Get all entities from stores
  // @ts-expect-error - project is not typed
  const tasks = useMemo(() => project.extraTaskStore?.query(() => true, true) || [], [project]);
  // @ts-expect-error - project is not typed
  const resources = useMemo(() => project.resourceStore?.query(() => true, true) || [], [project]);
  // @ts-expect-error - project is not typed
  const clients = useMemo(() => project.clientsStore?.query(() => true, true) || [], [project]);
  // @ts-expect-error - project is not typed
  const users = useMemo(() => project.boardUsersStore?.query(() => true, true) || [], [project]);

  // Filter entities based on search term
  const filterBySearch = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (entities: any[]) => {
      if (!searchTerm.trim()) return entities;
      return entities.filter((entity) => entity.name?.toLowerCase().includes(searchTerm.toLowerCase()));
    },
    [searchTerm]
  );

  const filteredTasks = useMemo(() => filterBySearch(tasks), [tasks, filterBySearch]);
  const filteredResources = useMemo(() => filterBySearch(resources), [resources, filterBySearch]);
  const filteredClients = useMemo(() => filterBySearch(clients), [clients, filterBySearch]);
  const filteredUsers = useMemo(() => filterBySearch(users), [users, filterBySearch]);

  // Check if entity is already linked
  const isLinked = useCallback(
    (type: string, entityId: string | number): boolean => {
      return linkedEntities.some((entity) => entity.id === `${type}-${entityId}`);
    },
    [linkedEntities]
  );

  // Toggle entity link
  const toggleLink = useCallback(
    (
      type: string,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      entityRecord: any,
      checked: boolean
    ) => {
      const entityId = `${type}-${entityRecord.id}`;

      if (checked) {
        // Add entity
        let entityRelationKey = "";
        let entityContactStoreKey = "";
        let entityNamePrefix = "";

        if (type === ENTITY_TYPE_DISPLAY[ENTITY_TYPES.TASK]) {
          entityRelationKey = "taskId";
          entityContactStoreKey = STORE_KEYS.taskContactStore;
        } else if (type === ENTITY_TYPE_DISPLAY[ENTITY_TYPES.RESOURCE]) {
          entityRelationKey = "resourceId";
          entityContactStoreKey = STORE_KEYS.resourceContactStore;
        } else if (type === ENTITY_TYPE_DISPLAY[ENTITY_TYPES.CLIENT]) {
          entityRelationKey = "clientId";
          entityContactStoreKey = STORE_KEYS.clientContactStore;
        } else if (type === ENTITY_TYPE_DISPLAY[ENTITY_TYPES.USER]) {
          entityRelationKey = "userId";
          entityContactStoreKey = STORE_KEYS.userContactStore;
          entityNamePrefix = "Board User: ";
        }

        const newLinkedEntity: ILinkedEntity = {
          id: entityId,
          name: entityNamePrefix + entityRecord.name,
          type,
          entityRecord,
          entityContactStoreKey,
          entityRelationKey
        };

        setLinkedEntities((prev) => [...prev, newLinkedEntity]);
      } else {
        // Remove entity
        setLinkedEntities((prev) => prev.filter((entity) => entity.id !== entityId));
      }
    },
    []
  );

  const handleSave = useCallback(() => {
    onSave(linkedEntities);
  }, [linkedEntities, onSave]);

  // Render entity list helper
  const renderEntityList = useCallback(
    (
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      items: any[],
      type: string,
      subtitleKey?: string
    ) => {
      return (
        <div>
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="rounded-full bg-muted/90 p-4 mb-3">{getEntityIcon(type)}</div>
              <p className="text-sm text-muted-foreground">No items found</p>
              {searchTerm && (
                <p className="text-xs text-muted-foreground mt-1">
                  Try a different search term
                </p>
              )}
            </div>
          ) : (
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            items.map((item: any, index: number) => {
              const isSelected = isLinked(type, item.id);

              return (
                <div
                  key={item.id}
                  onClick={() => toggleLink(type, item, !isSelected)}
                  className={cn(
                    "flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer transition-colors rounded-md",
                    "hover:bg-accent",
                    isSelected && "bg-accent",
                    index < items.length - 1 && "mb-1"
                  )}
                >
                  {type === ENTITY_TYPE_DISPLAY[ENTITY_TYPES.USER] ? (
                    <UserAvatar
                      user={{
                        id: item.id,
                        name: item?.name || "N/A",
                        profileImageURL: item?.profileImageURL,
                        archived: item?.archived,
                      }}
                      showName={true}
                      secondaryText={subtitleKey && item[subtitleKey]}
                      className="max-w-[350px]"
                    />
                  ) : (
                    <div>
                      <p className="text-sm font-normal text-foreground max-w-[320px] truncate">{item.name}</p>
                      {subtitleKey && item[subtitleKey] && <p className="text-xs text-muted-foreground truncate">{item[subtitleKey]}</p>}
                    </div>
                  )}

                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(checked) => toggleLink(type, item, checked as boolean)}
                    onClick={(e) => e.stopPropagation()}
                    className="flex-shrink-0"
                  />
                </div>
              );
            })
          )}
        </div>
      );
    },
    [isLinked, toggleLink, searchTerm]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 flex flex-col">
        <DialogHeader className="px-6 py-5">
          <DialogTitle className="flex items-center gap-2 font-normal">Link Entities</DialogTitle>
          <DialogDescription className="pt-1.5">
            Select entities to link to this contact
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="resources" className="flex-1 flex flex-col">
          <div className="flex flex-col px-6">
            {/* Search Bar */}
            <div className="relative py-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-10 bg-background border-input focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </div>

            {/* Tabs */}
            <TabsList className="w-full grid grid-cols-4 mb-4">
              <TabsTrigger value="resources" className="flex items-center gap-2 text-sm">
                {getEntityIcon(ENTITY_TYPES.RESOURCE)} Resources
              </TabsTrigger>
              <TabsTrigger value="tasks" className="flex items-center gap-2 text-sm">
                {getEntityIcon(ENTITY_TYPES.TASK)} Tasks
              </TabsTrigger>
              <TabsTrigger value="clients" className="flex items-center gap-2 text-sm">
                {getEntityIcon(ENTITY_TYPES.CLIENT)} Clients
              </TabsTrigger>
              <TabsTrigger value="users" className="flex items-center gap-2 text-sm">
                {getEntityIcon(ENTITY_TYPES.USER)} Users
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Tab Content */}
          <div className="px-6">
            <ScrollArea className="h-[250px] mb-4">
              <TabsContent value="resources" className="mt-0">
                {renderEntityList(filteredResources, ENTITY_TYPE_DISPLAY[ENTITY_TYPES.RESOURCE], "type")}
              </TabsContent>

              <TabsContent value="tasks" className="mt-0">
                {renderEntityList(filteredTasks, ENTITY_TYPE_DISPLAY[ENTITY_TYPES.TASK], "statusName")}
              </TabsContent>

              <TabsContent value="clients" className="mt-0">
                {renderEntityList(filteredClients, ENTITY_TYPE_DISPLAY[ENTITY_TYPES.CLIENT])}
              </TabsContent>

              <TabsContent value="users" className="mt-0">
                {renderEntityList(filteredUsers, ENTITY_TYPE_DISPLAY[ENTITY_TYPES.USER], "email")}
              </TabsContent>
            </ScrollArea>
          </div>

          {/* Footer with selected count */}
          <div className="flex items-center justify-between gap-3 px-6 pb-5 pt-5 border-t">
            <span className={cn(
              "text-sm",
              linkedEntities.length > 0 ? "text-foreground font-medium" : "text-muted-foreground"
            )}>
              {linkedEntities.length} selected
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} size="sm">
                Cancel
              </Button>
              <Button type="button" onClick={handleSave} size="sm">
                Save
              </Button>
            </div>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
